import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, CreditCard, Lock, Loader2, AlertCircle, ShoppingBag } from "lucide-react";
import { useCart, formatPrice } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Checkout() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { items, totalCents, totalItems, clearCart } = useCart();
  const [email, setEmail] = useState(user?.email || "");
  const [sdkReady, setSdkReady] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [squareConfig, setSquareConfig] = useState(null);

  // Sync email when user loads after initial render
  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);

  // Step 1: Fetch Square public config from backend
  useEffect(() => {
    api.get("/shop/config")
      .then(({ data }) => setSquareConfig(data))
      .catch(() => setError("Payment system is not configured. Please contact support."));
  }, []);

  // Step 2: Load Square Web Payments SDK script once config is available
  useEffect(() => {
    if (!squareConfig) return;

    const sdkUrl = squareConfig.environment === "production"
      ? "https://web.squarecdn.com/v1/square.js"
      : "https://sandbox.web.squarecdn.com/v1/square.js";

    if (window.Square) {
      setSdkReady(true);
      return;
    }

    const script = document.createElement("script");
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => setSdkReady(true);
    script.onerror = () => setError("Failed to load payment system. Please refresh.");
    document.head.appendChild(script);
  }, [squareConfig]);

  // Step 3: Initialize card fields
  useEffect(() => {
    if (!sdkReady || !window.Square || !squareConfig) return;
    let cancelled = false;

    async function init() {
      try {
        const payments = window.Square.payments(squareConfig.application_id, squareConfig.location_id);
        paymentsRef.current = payments;
        const card = await payments.card();
        if (cancelled) return;
        await card.attach("#square-card-container");
        cardRef.current = card;
        setCardReady(true);
      } catch (err) {
        if (!cancelled) {
          setError("Failed to initialize payment fields. Please refresh.");
        }
      }
    }

    init();
    return () => {
      cancelled = true;
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch {}
        cardRef.current = null;
      }
    };
  }, [sdkReady]);

  const handlePay = useCallback(async () => {
    if (!cardRef.current || processing) return;
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (items.length === 0) {
      setError("Your cart is empty.");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      // Step 1: Tokenize card (no raw card data touches our server)
      const tokenResult = await cardRef.current.tokenize();
      if (tokenResult.status !== "OK") {
        const msg = tokenResult.errors
          ? tokenResult.errors.map((e) => e.message).join("; ")
          : "Card verification failed. Please check your details.";
        throw new Error(msg);
      }

      // Step 2: Create order on our server (server computes prices from Square catalog)
      const { data: orderData } = await api.post("/shop/orders", {
        lines: items.map((item) => ({
          variation_id: item.variationId,
          quantity: item.quantity,
        })),
        guest_email: user ? undefined : email,
      });

      // Step 3: Submit payment token to our server
      const { data: paymentData } = await api.post("/shop/checkout/pay", {
        order_id: orderData.order_id,
        source_id: tokenResult.token,
      });

      if (paymentData.status === "COMPLETED" || paymentData.status === "APPROVED") {
        clearCart();
        toast.success("Payment successful!");
        nav(`/shop/order/${orderData.order_id}`);
      } else {
        throw new Error(`Payment status: ${paymentData.status}. Please try again.`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err.message ||
        "Payment failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }, [items, email, user, processing, clearCart, nav]);

  if (items.length === 0 && !processing) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ShoppingBag className="h-14 w-14 mx-auto text-[hsl(var(--muted-fg))] mb-4" />
          <h2 className="text-xl font-semibold">Nothing to checkout</h2>
          <p className="text-sm text-[hsl(var(--muted-fg))] mt-2">Add some items to your cart first.</p>
          <Link to="/shop">
            <Button className="mt-6 bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]" data-testid="checkout-back-to-shop">
              Browse Shop
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link
          to="/shop/cart"
          className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-fg))] hover:text-[hsl(var(--fg))] transition-colors mb-6"
          data-testid="checkout-back-link"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Cart
        </Link>

        <h1 className="text-2xl sm:text-3xl font-semibold text-[hsl(var(--fg))] mb-6" data-testid="checkout-page-title">
          Checkout
        </h1>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Payment Form */}
          <div className="lg:col-span-3 space-y-6">
            {/* Contact */}
            <Card className="border border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <Label htmlFor="checkout-email" className="text-sm mb-1.5 block">Email address</Label>
                <Input
                  id="checkout-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!user?.email}
                  data-testid="checkout-email-input"
                />
                {user?.email && (
                  <p className="text-xs text-[hsl(var(--muted-fg))] mt-1">Logged in as {user.email}</p>
                )}
              </CardContent>
            </Card>

            {/* Payment */}
            <Card className="border border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4 text-xs text-[hsl(var(--muted-fg))]">
                  <Lock className="h-3.5 w-3.5" />
                  <span>Card details are securely handled by Square. We never see your card number.</span>
                </div>

                {/* Square card fields container */}
                <div
                  id="square-card-container"
                  className="min-h-[80px] rounded-lg border border-[hsl(var(--border))] p-1"
                  data-testid="square-card-container"
                >
                  {!cardReady && (
                    <div className="flex items-center justify-center h-20">
                      <Skeleton className="h-10 w-full" />
                    </div>
                  )}
                </div>

                {squareConfig?.environment === "sandbox" && (
                  <p className="text-xs text-[hsl(38,92%,45%)] mt-3 font-medium" data-testid="checkout-sandbox-notice">
                    Sandbox Mode — Use card 4111 1111 1111 1111, CVV 111, any future date, zip 12345
                  </p>
                )}

                {error && (
                  <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-[hsl(6,72%,46%,0.08)] text-[hsl(6,72%,36%)] text-sm" data-testid="checkout-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <Card className="border border-[hsl(var(--border))] sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  {items.map((item) => (
                    <div key={item.variationId} className="flex justify-between gap-2">
                      <span className="text-[hsl(var(--fg))] truncate">
                        {item.productName}
                        {item.variationName !== "Regular" && ` (${item.variationName})`}
                        {item.quantity > 1 && ` x${item.quantity}`}
                      </span>
                      <span className="font-medium shrink-0">
                        {formatPrice(item.priceCents * item.quantity, item.currency)}
                      </span>
                    </div>
                  ))}
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-[hsl(var(--fg))]">Total</span>
                  <span className="text-xl font-bold text-[hsl(var(--fg))]" data-testid="checkout-total">
                    {formatPrice(totalCents)}
                  </span>
                </div>

                <Button
                  className="w-full min-h-[48px] mt-6 text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] hover:bg-[hsl(var(--primary))]/90 gap-2"
                  onClick={handlePay}
                  disabled={processing || !cardReady}
                  data-testid="checkout-pay-button"
                >
                  {processing ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</>
                  ) : (
                    <><Lock className="h-4 w-4" /> Pay {formatPrice(totalCents)}</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
